import React, { useState } from 'react';
import { ChevronDown, ChevronRight, GripVertical } from 'lucide-react';
import type { OrgUnit } from '@schemas/api';
import { useOrgStore } from '@store/orgStore';

interface OrgTreeNodeProps {
  unit: OrgUnit;
  onSelect: (unitId: string) => void;
  onToggleExpand: (unitId: string) => void;
  onDragStart: (e: React.DragEvent, unitId: string) => void;
  onDragOver: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent, targetUnitId: string) => void;
  canEdit: boolean;
  level?: number;
  isLast?: boolean;
}

export const OrgTreeNode: React.FC<OrgTreeNodeProps> = ({
  unit,
  onSelect,
  onToggleExpand,
  onDragStart,
  onDragOver,
  onDrop,
  canEdit,
  level = 0,
  isLast = false,
}) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const draggedUnitId = useOrgStore((state) => state.draggedUnitId);
  const selectedUnitId = useOrgStore((state) => state.selectedUnitId);
  const expandedNodeIds = useOrgStore((state) => state.expandedNodeIds);

  const isSelected = selectedUnitId === unit.id;
  const isExpanded = expandedNodeIds.has(unit.id);
  const hasChildren = unit.children && unit.children.length > 0;

  const handleDragOver = (e: React.DragEvent) => {
    if (!canEdit) return;
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
    onDragOver(e);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    if (!canEdit) return;
    e.stopPropagation();
    if (e.target === e.currentTarget) {
      setIsDragOver(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    if (!canEdit) return;
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    onDrop(e, unit.id);
  };

  const isDragging = draggedUnitId === unit.id;

  return (
    <div className="flex flex-col">
      {/* Node row */}
      <div className="flex items-stretch">
        {/* Tree connector lines */}
        {level > 0 && (
          <div className="flex items-stretch" style={{ width: `${level * 20}px` }}>
            {/* Vertical + horizontal connector for this node */}
            <div className="flex items-stretch" style={{ width: `${(level - 1) * 20}px` }} />
            <div className="relative w-5 flex-shrink-0">
              {/* Vertical line */}
              <div
                className={`absolute left-[9px] top-0 w-px bg-gray-300 ${isLast ? 'h-1/2' : 'h-full'}`}
              />
              {/* Horizontal branch */}
              <div className="absolute left-[9px] top-1/2 h-px w-[11px] bg-gray-300" />
            </div>
          </div>
        )}

        {/* Node content */}
        <div
          className={`flex-1 flex items-center gap-2 px-2 py-1.5 cursor-pointer group rounded transition-colors ${
            isSelected
              ? 'bg-primary-50 border-l-2 border-primary-500'
              : 'hover:bg-gray-100'
          } ${
            isDragOver
              ? 'bg-primary-100 border-2 border-dashed border-primary-400'
              : ''
          } ${isDragging ? 'opacity-50' : ''}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => onSelect(unit.id)}
        >
          {/* Expand toggle */}
          <div className="w-5 flex justify-center flex-shrink-0">
            {hasChildren ? (
              <button
                className="p-0 hover:bg-gray-200 rounded"
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleExpand(unit.id);
                }}
              >
                {isExpanded ? (
                  <ChevronDown size={16} className="text-gray-600" />
                ) : (
                  <ChevronRight size={16} className="text-gray-600" />
                )}
              </button>
            ) : (
              <div className="w-4" />
            )}
          </div>

          {/* Drag handle */}
          {canEdit && (
            <div
              className="opacity-0 group-hover:opacity-100 cursor-grab active:cursor-grabbing flex-shrink-0"
              draggable
              onDragStart={(e) => {
                e.stopPropagation();
                onDragStart(e, unit.id);
              }}
            >
              <GripVertical size={16} className="text-gray-400" />
            </div>
          )}

          {/* Unit info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-medium text-gray-900 truncate">
                {unit.name}
              </span>
              <span className="text-xs bg-gray-200 text-gray-700 px-2 py-0.5 rounded whitespace-nowrap">
                {unit.unit_type}
              </span>
            </div>
            {unit.description && (
              <p className="text-xs text-gray-500 truncate">
                {unit.description}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Children (if expanded) */}
      {hasChildren && isExpanded && (
        <div className="relative">
          {/* Continuous vertical line for parent */}
          {level > 0 && !isLast && (
            <div
              className="absolute top-0 bottom-0 w-px bg-gray-300"
              style={{ left: `${(level - 1) * 20 + 9}px` }}
            />
          )}
          {unit.children!.map((child, index) => (
            <OrgTreeNode
              key={child.id}
              unit={child}
              onSelect={onSelect}
              onToggleExpand={onToggleExpand}
              onDragStart={onDragStart}
              onDragOver={onDragOver}
              onDrop={onDrop}
              canEdit={canEdit}
              level={level + 1}
              isLast={index === unit.children!.length - 1}
            />
          ))}
        </div>
      )}
    </div>
  );
};
